// Added by Sejune Oh on 2019-03-29
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { action, makeObservable, observable } from 'mobx'

import Rootstore from '../../index'
import DataStorageStoreBase from '../../base/DataStorageStoreBase'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
interface StudyStatusBaseElementProps {
  id?: string
  name?: string
  code?: number
  color?: string
  description?: string
  proxy_token?: string
  created_at?: string
  updated_at?: string
  activated?: boolean
}

export interface StudyStatusElementProps extends StudyStatusBaseElementProps {
  post_action?: PostActionType
  username?: string
  user_email?: string
  user_display_name?: string
  target_status_id?: string
  status_name?: string
  status_code?: number
  status_color?: string
  status_description?: string
  added_by_sysytem?: boolean
}

interface removeReqProps {
  id: string
  username: string
  user_email: string
  user_display_name: string
  proxy_token: string
  target_status_id?: string
}

type PostActionType = 'NONE' | 'CLEAR' | 'CHANGE'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class DataStorageStudyStatusStore extends DataStorageStoreBase<StudyStatusElementProps> {
  totalCount: number = 0
  elements: Array<StudyStatusElementProps> = []
  cachedElements: Array<StudyStatusElementProps> = []
  root?: Rootstore

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  constructor(root: Rootstore) {
    super({
      storeName: 'DS_StudyStatus',
      apiSubUrl: 'config/study-statuses',
      _root: root,
    })
    this.root = root
    makeObservable(this, {
      totalCount: observable,
      elements: observable,
      cachedElements: observable,
      _setTotalCount: action,
      setElements: action,
      cachedStore: action,
      clearCachedStore: action,
    })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _removeElement = async ({ ...props }: removeReqProps, postAction: PostActionType) => {
    return await super.postCustom({
      subUrl: `/${props.id}`,
      element: {
        ...props,
        post_action: postAction,
      },
    })
  }

  getElements = async (path?: string) => {
    const rtnData = await super.getAll(path)
    await this._setTotalCount(parseInt(rtnData.total_count as string))
    return rtnData.items as Array<StudyStatusElementProps>
  }

  getEnabledStudyStatus = async () => {
    if (!this._proxyToken) return []

    const filter = { $op: 'and', activated: true }
    const path = this.getQueryPath({ filter: JSON.stringify(filter), orderBy: 'code', order: 'asc' })
    const { items } = await this.getAll(path)
    return items
  }

  getDisabledStudyStatus = async () => {
    if (!this._proxyToken) return []

    const filter = { $op: 'and', activated: false }
    const path = this.getQueryPath({ filter: JSON.stringify(filter), orderBy: 'code', order: 'asc' })
    const { items } = await this.getAll(path)
    return items
  }

  addElement = async (element: StudyStatusElementProps) => {
    const newElement = await super.add(element)
    return newElement.id
  }

  updateElement = async (id: string, element: StudyStatusElementProps) => {
    return super.update(id, element)
  }

  removeElement = async ({ target_status_id = '0', ...props }: removeReqProps) => {
    return this._removeElement({ target_status_id, ...props }, 'NONE')
  }

  removeNclear = async ({ target_status_id = '0', ...props }: removeReqProps) => {
    return this._removeElement({ target_status_id, ...props }, 'CLEAR')
  }

  removeNchange = async ({ ...props }: removeReqProps) => {
    return this._removeElement({ ...props }, 'CHANGE')
  }

  removeAll = async (proxy_token: string) => {
    return await super.postCustom({
      subUrl: ':clearall',
      element: {
        proxy_token,
      },
    })
  }

  checkDuplicatedName = async (name: string) => {
    return super.checkDuplicatedByField({
      field: 'name',
      value: name,
    })
  }

  checkDuplicatedCode = async (code: number) => {
    return super.checkDuplicatedByField({
      field: 'code',
      value: code.toString(),
    })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _setTotalCount = (count: number) => {
    this.totalCount = count
  }

  setElements = (elements: Array<StudyStatusElementProps>) => {
    this.elements = elements
  }

  cachedStore = (elements: Array<StudyStatusElementProps>) => {
    this.cachedElements = elements
  }

  clearCachedStore = () => {
    this.cachedElements = []
  }
}
